// ===== Interfaces =====

import {
    IAudioNoteTranscriptionJobItem,
}                                   from '../../../interfaces';

// ===== Enums =====

import {
    AUDIO_TRANSCRIPTION_JOB_STATUS,
}                                   from '../../../enums';

const normalizeTranscriptionJobUpdated = (
    updated: number | null | undefined,
): number | null => (
    typeof updated === 'number' && Number.isFinite(updated)
        ? updated
        : null
);

const normalizeTranscriptionJobProgress = (
    progress: number | null | undefined,
): number | null => (
    typeof progress === 'number' && Number.isFinite(progress)
        ? Math.max(0, Math.min(100, Math.round(progress)))
        : null
);

const isTerminalTranscriptionStatus = (
    status: AUDIO_TRANSCRIPTION_JOB_STATUS | null | undefined,
): boolean => (
    status === AUDIO_TRANSCRIPTION_JOB_STATUS.completed
    || status === AUDIO_TRANSCRIPTION_JOB_STATUS.failed
    || status === AUDIO_TRANSCRIPTION_JOB_STATUS.skipped
);

const readTranscriptionStatusRank = (
    status: AUDIO_TRANSCRIPTION_JOB_STATUS | null | undefined,
): number => {
    switch (status) {
    case AUDIO_TRANSCRIPTION_JOB_STATUS.queued:
        return 1;
    case AUDIO_TRANSCRIPTION_JOB_STATUS.offloaded:
        return 2;
    case AUDIO_TRANSCRIPTION_JOB_STATUS.processing:
        return 3;
    case AUDIO_TRANSCRIPTION_JOB_STATUS.completed:
    case AUDIO_TRANSCRIPTION_JOB_STATUS.failed:
    case AUDIO_TRANSCRIPTION_JOB_STATUS.skipped:
        return 4;
    default:
        return 0;
    }
};

/**
 * The editor can receive transcription updates from both the direct media doc
 * listener and the ping-triggered callable fetch. Keep the newer job state so
 * the visible badge never regresses when those two sources resolve out of
 * order.
 */
export const resolveMostRecentAudioNoteTranscriptionJob = ({
    current,
    incoming,
}: {
    current: IAudioNoteTranscriptionJobItem | null | undefined,
    incoming: IAudioNoteTranscriptionJobItem | null | undefined,
}): IAudioNoteTranscriptionJobItem | null | undefined => {
    if (incoming == null) {
        return current ?? incoming;
    }

    if (current == null) {
        return incoming;
    }

    const currentUpdated = normalizeTranscriptionJobUpdated(current.updated);
    const incomingUpdated = normalizeTranscriptionJobUpdated(incoming.updated);
    if (
        currentUpdated != null
        && incomingUpdated != null
        && currentUpdated !== incomingUpdated
    ) {
        return incomingUpdated > currentUpdated
            ? incoming
            : current;
    }

    const currentJobId = typeof current.jobId === 'string'
        ? current.jobId
        : null;
    const incomingJobId = typeof incoming.jobId === 'string'
        ? incoming.jobId
        : null;
    const isSameActiveJob = (
        Boolean(currentJobId)
        && Boolean(incomingJobId)
        && currentJobId === incomingJobId
        && !isTerminalTranscriptionStatus(current.status)
        && !isTerminalTranscriptionStatus(incoming.status)
    );
    if (isSameActiveJob) {
        const currentProgress = normalizeTranscriptionJobProgress(current.progress);
        const incomingProgress = normalizeTranscriptionJobProgress(incoming.progress);
        if (
            currentProgress != null
            && incomingProgress != null
            && currentProgress !== incomingProgress
        ) {
            return incomingProgress > currentProgress
                ? incoming
                : current;
        }
    }

    if (currentUpdated != null || incomingUpdated != null) {
        return incomingUpdated != null
            ? incoming
            : current;
    }

    const currentStatusRank = readTranscriptionStatusRank(current.status);
    const incomingStatusRank = readTranscriptionStatusRank(incoming.status);
    if (currentStatusRank !== incomingStatusRank) {
        return incomingStatusRank > currentStatusRank
            ? incoming
            : current;
    }

    return incoming;
};
